/**
 * Photometric corrections on `ImageData` alone, so they run unchanged on the page and in the
 * engine's Worker.
 */

/**
 * Photographic enhancement settings, each normalised to 0..1. Purely photometric — like the
 * ScanRetto prototype's "integrity guarantee", no pixel is invented or reconstructed, only global
 * tone/detail adjustments are applied.
 */
export interface Enhancements {
  readonly illumination: number;
  readonly contrast: number;
  readonly sharpness: number;
  readonly noise: number;
}

/** Sensible defaults for a document photo — mild lighting evening-out, contrast, and sharpening. */
export const DEFAULT_ENHANCEMENTS: Enhancements = {
  illumination: 0.4,
  contrast: 0.2,
  sharpness: 0.25,
  noise: 0.15,
};

/** Separable box blur over a Float32Array (used by the illumination and sharpness passes). */
function boxBlurGray(
  a: Float32Array,
  w: number,
  h: number,
  r: number,
  passes: number,
): Float32Array {
  const b = new Float32Array(w * h);
  const n = 2 * r + 1;
  for (let p = 0; p < passes; p++) {
    for (let y = 0; y < h; y++) {
      const row = y * w;
      let acc = 0;
      for (let x = -r; x <= r; x++) acc += a[row + Math.min(w - 1, Math.max(0, x))] ?? 0;
      for (let x = 0; x < w; x++) {
        b[row + x] = acc / n;
        acc += (a[row + Math.min(w - 1, x + r + 1)] ?? 0) - (a[row + Math.max(0, x - r)] ?? 0);
      }
    }
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let y = -r; y <= r; y++) acc += b[Math.min(h - 1, Math.max(0, y)) * w + x] ?? 0;
      for (let y = 0; y < h; y++) {
        a[y * w + x] = acc / n;
        acc += (b[Math.min(h - 1, y + r + 1) * w + x] ?? 0) - (b[Math.max(0, y - r) * w + x] ?? 0);
      }
    }
  }
  return a;
}

const luminanceOf = (d: Uint8ClampedArray, j: number): number =>
  0.299 * (d[j] ?? 0) + 0.587 * (d[j + 1] ?? 0) + 0.114 * (d[j + 2] ?? 0);

function luminanceMap(od: Uint8ClampedArray, count: number): Float32Array {
  const lum = new Float32Array(count);
  for (let i = 0; i < count; i++) lum[i] = luminanceOf(od, i * 4);
  return lum;
}

/** 1 — noise reduction: light neighbour averaging, weighted by the slider. */
function reduceNoise(image: ImageData, noise: number): void {
  const { width: w, height: h, data: od } = image;
  const tmp = new Uint8ClampedArray(od);
  const mix = noise * 0.6;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = (y * w + x) * 4;
      for (let c = 0; c < 3; c++) {
        const avg =
          ((tmp[i + c - 4] ?? 0) +
            (tmp[i + c + 4] ?? 0) +
            (tmp[i + c - w * 4] ?? 0) +
            (tmp[i + c + w * 4] ?? 0) +
            (tmp[i + c] ?? 0) * 4) /
          8;
        od[i + c] = (od[i + c] ?? 0) * (1 - mix) + avg * mix;
      }
    }
  }
}

/** 2 — illumination evening-out: divide by the low-frequency light map. */
function evenIllumination(image: ImageData, illumination: number): void {
  const { width: w, height: h, data: od } = image;
  const count = w * h;
  const lum = luminanceMap(od, count);
  const r = Math.max(8, Math.round(Math.min(w, h) / 14));
  const low = boxBlurGray(lum, w, h, r, 2);
  let mean = 0;
  for (let i = 0; i < count; i++) mean += low[i] ?? 0;
  mean /= count;
  for (let i = 0; i < count; i++) {
    const gain = Math.min(2.2, Math.max(0.55, mean / ((low[i] ?? 0) + 1)));
    const f = 1 + (gain - 1) * illumination;
    const j = i * 4;
    od[j] = (od[j] ?? 0) * f;
    od[j + 1] = (od[j + 1] ?? 0) * f;
    od[j + 2] = (od[j + 2] ?? 0) * f;
  }
}

/** 3 — linear contrast around mid-grey. */
function addContrast(image: ImageData, contrast: number): void {
  const od = image.data;
  const f = 1 + contrast * 0.7;
  for (let i = 0; i < image.width * image.height * 4; i += 4) {
    od[i] = ((od[i] ?? 0) - 128) * f + 128;
    od[i + 1] = ((od[i + 1] ?? 0) - 128) * f + 128;
    od[i + 2] = ((od[i + 2] ?? 0) - 128) * f + 128;
  }
}

/** 4 — sharpening: unsharp mask on luminance. */
function sharpen(image: ImageData, sharpness: number): void {
  const { width: w, height: h, data: od } = image;
  const count = w * h;
  const lum = luminanceMap(od, count);
  const orig = Float32Array.from(lum);
  const blur = boxBlurGray(lum, w, h, 1, 1);
  const amount = sharpness * 1.4;
  for (let i = 0; i < count; i++) {
    const d = ((orig[i] ?? 0) - (blur[i] ?? 0)) * amount;
    const j = i * 4;
    od[j] = (od[j] ?? 0) + d;
    od[j + 1] = (od[j + 1] ?? 0) + d;
    od[j + 2] = (od[j + 2] ?? 0) + d;
  }
}

/**
 * Applies the photographic enhancement passes (noise reduction, illumination evening-out, contrast,
 * sharpening) to `image` IN PLACE and returns it: pass a copy (`readPixels` returns one), so
 * re-running with different settings always starts from the same rectified base (ported from
 * ScanRetto's `applyFilters`). Passes below their threshold are skipped.
 */
export function enhancePixels(image: ImageData, settings: Enhancements): ImageData {
  const { illumination, contrast, sharpness, noise } = settings;
  if (noise > 0.01) reduceNoise(image, noise);
  if (illumination > 0.01) evenIllumination(image, illumination);
  if (contrast > 0.01) addContrast(image, contrast);
  if (sharpness > 0.01) sharpen(image, sharpness);
  return image;
}

/** Fine-rotation limit (degrees) offered to the user for nudging a slightly-tilted document level. */
export const MAX_FINE_ROTATION_DEG = 15;

/** The colour treatment applied after the corrections: as photographed, grey, or black and white. */
export type ColourMode = 'colour' | 'grey' | 'bw';

const blackOrWhite = (lum: number, mean: number): number => (lum < mean * 0.85 ? 0 : 255);

/**
 * Applies `mode` to `image` IN PLACE and returns it. `bw` thresholds at the image's mean luminance,
 * which after the illumination pass separates ink from paper well enough for a document; no pixel
 * is invented.
 */
export function applyColourMode(image: ImageData, mode: ColourMode): ImageData {
  if (mode === 'colour') return image;
  const d = image.data;
  const count = image.width * image.height;
  let mean = 0;
  for (let i = 0; i < count; i++) {
    const j = i * 4;
    const lum = 0.299 * (d[j] ?? 0) + 0.587 * (d[j + 1] ?? 0) + 0.114 * (d[j + 2] ?? 0);
    d[j] = lum;
    mean += lum;
  }
  mean /= Math.max(1, count);
  for (let i = 0; i < count; i++) {
    const j = i * 4;
    const lum = d[j] ?? 0;
    const v = mode === 'bw' ? blackOrWhite(lum, mean) : lum;
    d[j] = v;
    d[j + 1] = v;
    d[j + 2] = v;
  }
  return image;
}
